import { type FC } from "react";
import {
  Box,
  LinearProgress,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Stream } from "@/partner/entities/streams";
import {
  RecordingTrimErrorCodeType,
  RecordingTrimStatusType,
} from "@/types/graphql/partner.generated";

type ActiveTrimStatus = Exclude<
  RecordingTrimStatusType,
  RecordingTrimStatusType.DoneType | RecordingTrimStatusType.FailedType
>;

// Явная мапа ключей шагов: ключ t() только статический литерал.
const STEP_LABEL_KEYS: Record<ActiveTrimStatus, string> = {
  [RecordingTrimStatusType.PendingType]: "streams.recordingTrim.step.pending",
  [RecordingTrimStatusType.DownloadingType]:
    "streams.recordingTrim.step.downloading",
  [RecordingTrimStatusType.CopyingType]: "streams.recordingTrim.step.copying",
  [RecordingTrimStatusType.ClippingType]: "streams.recordingTrim.step.clipping",
  // Полный текст шага (с префиксом), см. ветку CUTTING ниже.
  [RecordingTrimStatusType.CuttingType]:
    "streams.recordingTrim.step.cuttingNoPercent",
};

// Коды ошибок нарезчика: запись при них привязана целиком.
const ERROR_LABEL_KEYS: Partial<Record<RecordingTrimErrorCodeType, string>> = {
  [RecordingTrimErrorCodeType.TooLargeType]:
    "streams.recordingTrim.error.TOO_LARGE",
  [RecordingTrimErrorCodeType.WorkerLostType]:
    "streams.recordingTrim.error.WORKER_LOST",
  [RecordingTrimErrorCodeType.CutErrorType]:
    "streams.recordingTrim.error.CUT_ERROR",
};

interface StreamRecordingTrimStatusProps {
  stream: Stream;
  /** Отступы под место показа (в карточке плеера без CardContent). */
  sx?: SxProps<Theme>;
}

// Строка статуса обрезки записи эфира до окна трансляции. Показывается,
// пока обрезка идёт или если она упала (запись тогда привязана целиком);
// после DONE и без recordingTrim ничего не рендерит.
export const StreamRecordingTrimStatus: FC<StreamRecordingTrimStatusProps> = ({
  stream,
  sx,
}) => {
  const { t } = useTranslation();
  const trim = stream.recordingTrim;

  if (!trim || trim.status === RecordingTrimStatusType.DoneType) return null;

  if (trim.status === RecordingTrimStatusType.FailedType) {
    if (trim.error?.code === RecordingTrimErrorCodeType.TooLongType) {
      return (
        <Typography variant="body2" color="text.secondary" sx={sx}>
          {t("streams.recordingTrim.tooLong")}
        </Typography>
      );
    }
    const errorKey = trim.error?.code && ERROR_LABEL_KEYS[trim.error.code];
    if (errorKey) {
      return (
        <Typography variant="body2" color="warning.main" sx={sx}>
          {t(errorKey)}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color="warning.main" sx={sx}>
        {t("streams.recordingTrim.failed", {
          reason: trim.error?.message ?? "",
        })}
      </Typography>
    );
  }

  const percent =
    typeof trim.progress === "number"
      ? Math.min(100, Math.max(0, Math.round(trim.progress)))
      : null;

  // Шаги нарезчика с известным процентом: полный текст и determinate-полоса.
  if (percent !== null && trim.status === RecordingTrimStatusType.CuttingType) {
    return (
      <Box sx={sx}>
        <Typography variant="body2" color="text.secondary">
          {t("streams.recordingTrim.step.cutting", { percent })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{ mt: 0.5 }}
        />
      </Box>
    );
  }
  if (percent !== null && trim.status === RecordingTrimStatusType.CopyingType) {
    return (
      <Box sx={sx}>
        <Typography variant="body2" color="text.secondary">
          {t("streams.recordingTrim.step.uploadingToPlayerPercent", {
            percent,
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{ mt: 0.5 }}
        />
      </Box>
    );
  }
  if (trim.status === RecordingTrimStatusType.CuttingType) {
    return (
      <Typography variant="body2" color="text.secondary" sx={sx}>
        {t("streams.recordingTrim.step.cuttingNoPercent")}
      </Typography>
    );
  }
  // До первого замера Cloudflare: тот же шаг, что и с процентом.
  if (trim.status === RecordingTrimStatusType.CopyingType) {
    return (
      <Typography variant="body2" color="text.secondary" sx={sx}>
        {t("streams.recordingTrim.step.uploadingToPlayer")}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={sx}>
      {t("streams.recordingTrim.active", {
        step: t(STEP_LABEL_KEYS[trim.status]),
      })}
    </Typography>
  );
};
